/**
 * Agent intertwine — who invokes whom, and which MCP tools they share.
 *
 * Edges come from volcano_config.workflows (every key) and volcano_workflow:
 *   provider:'agent' | type:'agent-call' | invoke_agent → agent→agent
 *   provider:'mcp' | mcp_tool / known MCP tool names → agent→tool
 *
 * Interpolation targets (`{{...}}`) are not static edges.
 * This module does not infer health.
 */

import { getWorkflowSteps, isTerminalStubAgent } from './agentHonesty';

export type IntertwineAgent = {
  agent_id?: string | null;
  id?: string | null;
  name?: string | null;
  description?: string | null;
  honesty_stage?: string | null;
  terminal_stub?: boolean | null;
  aliases?: string[] | null;
  proposed_key?: string | null;
  metadata?: { proposed_key?: string | null } | null;
  volcano_workflow?: unknown;
  volcano_config?: { workflows?: unknown } | null;
};

export type IntertwineEdgeKind = 'invokes_agent' | 'invokes_tool';

export type IntertwineEdge = {
  id: string;
  source: string;
  target: string;
  kind: IntertwineEdgeKind;
  label: string;
  workflow?: string;
  step_name?: string;
  tool_name?: string;
  mcp_server?: string;
};

export type IntertwineNode = {
  id: string;
  kind: 'agent' | 'tool';
  label: string;
  tool_name?: string;
  mcp_server?: string;
  stub?: boolean;
};

export type DownstreamTool = {
  id: string;
  tool_name: string;
  mcp_server?: string;
};

export type AgentIntertwine = {
  agentId: string;
  connectionCount: number;
  downstreamAgents: string[];
  downstreamTools: DownstreamTool[];
  upstreamAgents: string[];
  edges: IntertwineEdge[];
};

const INTERPOLATION = /\{\{/;

export function agentIdOf(agent: IntertwineAgent | null | undefined): string {
  return String(agent?.agent_id || agent?.id || '').trim();
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

export function isStaticRef(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (INTERPOLATION.test(trimmed)) return false;
  if (trimmed === 'null' || trimmed === 'undefined') return false;
  return true;
}

export function toolNodeId(toolName: string, mcpServer?: string | null): string {
  const tool = String(toolName || '').trim();
  const server = String(mcpServer || '').trim();
  return server ? `tool:${server}/${tool}` : `tool:${tool}`;
}

function stepProvider(step: Record<string, unknown>): string {
  return String(step.provider || step.type || '').trim().toLowerCase();
}

function stepName(step: Record<string, unknown>): string {
  return String(step.name || step.id || '').trim();
}

function extractStaticAgentId(step: Record<string, unknown>): string | null {
  const input = asRecord(step.input);
  const candidates = [step.agent_id, step.agentId, input?.agent_id, input?.agentId];
  for (const candidate of candidates) {
    if (isStaticRef(candidate)) return candidate.trim();
  }
  return null;
}

function extractMcpTool(step: Record<string, unknown>): { tool: string; server?: string } | null {
  const input = asRecord(step.input);
  const tool = String(step.mcp_tool || step.tool || input?.mcp_tool || input?.tool || '').trim();
  const server = String(step.mcp_server || step.server || input?.mcp_server || '').trim();
  if (!tool || INTERPOLATION.test(tool)) return null;
  return {
    tool,
    server: server && !INTERPOLATION.test(server) ? server : undefined,
  };
}

function isAgentInvokeStep(step: Record<string, unknown>): boolean {
  const provider = stepProvider(step);
  const name = stepName(step).toLowerCase();
  const tool = String(step.mcp_tool || '').toLowerCase();
  if (provider === 'agent' || provider === 'agent-call' || provider === 'invoke_agent') return true;
  if (name === 'invoke_agent' || tool === 'invoke_agent') return true;
  return false;
}

function isMcpInvokeStep(step: Record<string, unknown>): boolean {
  const provider = stepProvider(step);
  if (provider === 'mcp') return true;
  return Boolean(step.mcp_tool || step.mcp_server);
}

function workflowSteps(workflow: unknown): unknown[] {
  if (Array.isArray(workflow)) return workflow.filter(Boolean);
  const rec = asRecord(workflow);
  if (rec && Array.isArray(rec.steps)) return rec.steps.filter(Boolean);
  return [];
}

export function iterateWorkflowNamedSteps(
  agent: IntertwineAgent | null | undefined,
): Array<{ workflow: string; step: Record<string, unknown> }> {
  const out: Array<{ workflow: string; step: Record<string, unknown> }> = [];
  if (!agent) return out;

  const workflows = agent.volcano_config?.workflows;
  if (Array.isArray(workflows)) {
    workflows.forEach((workflow, index) => {
      const rec = asRecord(workflow);
      const name = String(rec?.name || `workflow_${index + 1}`);
      for (const raw of workflowSteps(workflow)) {
        const step = asRecord(raw);
        if (step) out.push({ workflow: name, step });
      }
    });
    return out;
  }

  if (workflows && typeof workflows === 'object') {
    for (const [name, workflow] of Object.entries(workflows as Record<string, unknown>)) {
      for (const raw of workflowSteps(workflow)) {
        const step = asRecord(raw);
        if (step) out.push({ workflow: name, step });
      }
    }
    return out;
  }

  for (const raw of getWorkflowSteps(agent)) {
    const step = asRecord(raw);
    if (step) out.push({ workflow: 'default', step });
  }
  return out;
}

function edgeId(edge: Omit<IntertwineEdge, 'id'>): string {
  return `${edge.source}->${edge.target}:${edge.kind}:${edge.tool_name || edge.label}`;
}

export function collectInvokeEdgesFromAgent(agent: IntertwineAgent | null | undefined): IntertwineEdge[] {
  const selfId = agentIdOf(agent);
  if (!selfId || !agent) return [];

  const edges: IntertwineEdge[] = [];
  const seen = new Set<string>();

  const add = (partial: Omit<IntertwineEdge, 'id'>) => {
    if (!partial.target || (partial.kind === 'invokes_agent' && partial.target === selfId)) return;
    const id = edgeId(partial);
    if (seen.has(id)) return;
    seen.add(id);
    edges.push({ ...partial, id });
  };

  for (const { workflow, step } of iterateWorkflowNamedSteps(agent)) {
    const name = stepName(step);

    if (isAgentInvokeStep(step)) {
      const target = extractStaticAgentId(step);
      if (target) {
        add({
          source: selfId,
          target,
          kind: 'invokes_agent',
          label: name || 'invoke_agent',
          workflow,
          step_name: name,
        });
      }
    }

    if (isMcpInvokeStep(step)) {
      const mcp = extractMcpTool(step);
      if (mcp) {
        add({
          source: selfId,
          target: toolNodeId(mcp.tool, mcp.server),
          kind: 'invokes_tool',
          label: mcp.tool,
          workflow,
          step_name: name,
          tool_name: mcp.tool,
          mcp_server: mcp.server,
        });
      }
    }
  }

  return edges;
}

export function connectionCount(agent: IntertwineAgent | null | undefined): number {
  return new Set(collectInvokeEdgesFromAgent(agent).map((edge) => edge.target)).size;
}

export function emptyIntertwine(agentId = ''): AgentIntertwine {
  return {
    agentId,
    connectionCount: 0,
    downstreamAgents: [],
    downstreamTools: [],
    upstreamAgents: [],
    edges: [],
  };
}

export function buildIntertwineGraph(agents: IntertwineAgent[] | null | undefined): {
  nodes: IntertwineNode[];
  edges: IntertwineEdge[];
  byAgent: Map<string, AgentIntertwine>;
} {
  const list = Array.isArray(agents) ? agents : [];
  const agentNodes = new Map<string, IntertwineNode>();
  const toolNodes = new Map<string, IntertwineNode>();
  const allEdges: IntertwineEdge[] = [];
  const seenEdges = new Set<string>();

  for (const agent of list) {
    const id = agentIdOf(agent);
    if (!id) continue;
    agentNodes.set(id, {
      id,
      kind: 'agent',
      label: String(agent.name || id),
      stub: isTerminalStubAgent(agent),
    });
  }

  for (const agent of list) {
    for (const edge of collectInvokeEdgesFromAgent(agent)) {
      if (seenEdges.has(edge.id)) continue;
      seenEdges.add(edge.id);
      allEdges.push(edge);
      if (edge.kind === 'invokes_tool' && !toolNodes.has(edge.target)) {
        toolNodes.set(edge.target, {
          id: edge.target,
          kind: 'tool',
          label: edge.tool_name || edge.label,
          tool_name: edge.tool_name,
          mcp_server: edge.mcp_server,
        });
      }
      if (edge.kind === 'invokes_agent' && !agentNodes.has(edge.target)) {
        agentNodes.set(edge.target, { id: edge.target, kind: 'agent', label: edge.target });
      }
    }
  }

  const incoming = new Map<string, Set<string>>();
  for (const edge of allEdges) {
    if (edge.kind !== 'invokes_agent') continue;
    if (!incoming.has(edge.target)) incoming.set(edge.target, new Set());
    incoming.get(edge.target)!.add(edge.source);
  }

  const byAgent = new Map<string, AgentIntertwine>();
  for (const agent of list) {
    const id = agentIdOf(agent);
    if (!id) continue;
    const mine = allEdges.filter((edge) => edge.source === id);
    const downstreamAgents = [...new Set(
      mine.filter((edge) => edge.kind === 'invokes_agent').map((edge) => edge.target),
    )];
    const seenTools = new Set<string>();
    const downstreamTools: DownstreamTool[] = [];
    for (const edge of mine) {
      if (edge.kind !== 'invokes_tool' || seenTools.has(edge.target)) continue;
      seenTools.add(edge.target);
      downstreamTools.push({
        id: edge.target,
        tool_name: edge.tool_name || edge.label,
        mcp_server: edge.mcp_server,
      });
    }
    byAgent.set(id, {
      agentId: id,
      connectionCount: new Set(mine.map((edge) => edge.target)).size,
      downstreamAgents,
      downstreamTools,
      upstreamAgents: [...(incoming.get(id) || [])],
      edges: mine,
    });
  }

  return {
    nodes: [...agentNodes.values(), ...toolNodes.values()],
    edges: allEdges,
    byAgent,
  };
}

export function getAgentIntertwine(
  agent: IntertwineAgent | null | undefined,
  roster: IntertwineAgent[] | null | undefined = [],
): AgentIntertwine {
  const id = agentIdOf(agent);
  if (!id || !agent) return emptyIntertwine(id);
  const list = Array.isArray(roster) && roster.length ? roster : [agent];
  const hasSelf = list.some((row) => agentIdOf(row) === id);
  const graph = buildIntertwineGraph(hasSelf ? list : [...list, agent]);
  return graph.byAgent.get(id) || emptyIntertwine(id);
}

export function asStringList(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((item) => String(item || '').trim()).filter(Boolean);
  if (typeof value === 'string' && value.trim()) return [value.trim()];
  return [];
}

export function agentAliases(agent: IntertwineAgent | null | undefined): string[] {
  const id = agentIdOf(agent);
  const aliases = new Set<string>();
  for (const value of asStringList(agent?.aliases)) aliases.add(value);
  const proposed = String(agent?.metadata?.proposed_key || agent?.proposed_key || '').trim();
  if (proposed) aliases.add(proposed);
  if (id.includes('--')) {
    const suffix = id.split('--').slice(-1)[0];
    if (suffix && suffix !== id) aliases.add(suffix);
  }
  aliases.delete(id);
  return [...aliases];
}

export function resolveAgentSlug(
  slug: string | null | undefined,
  roster: IntertwineAgent[] | null | undefined,
): string | null {
  const needle = String(slug || '').trim().toLowerCase();
  if (!needle) return null;
  const list = Array.isArray(roster) ? roster : [];

  const exact = list.find((agent) => agentIdOf(agent).toLowerCase() === needle);
  if (exact) return agentIdOf(exact);

  const byAlias = list.find((agent) => agentAliases(agent).some((alias) => alias.toLowerCase() === needle));
  if (byAlias) return agentIdOf(byAlias);

  const bySuffix = list.filter((agent) => {
    const id = agentIdOf(agent).toLowerCase();
    return id.endsWith(`--${needle}`) || id.endsWith(`-${needle}`);
  });
  if (bySuffix.length === 1) return agentIdOf(bySuffix[0]);
  return null;
}

export function asDisplayText(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map((item) => String(item)).join('\n');
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '';
  }
}
